import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import WeekSelector from "../weekSelector";
import { useTranslation } from "react-i18next";
import { Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const DriverReportsTable = () => {
  const { t } = useTranslation();
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchData = async (week) => {
    const token = localStorage.getItem("token");
    console.log("🔍 Завантаження даних для тижня:", week);
    
    if (!token) {
      console.error("❌ Токен відсутній!");
      alert("Помилка аутентифікації. Будь ласка, увійдіть знову.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get("/api/driver-reports-table", {
        params: { week },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log("✅ Отримано дані:", res.data);
      console.log("Тип даних:", typeof res.data, "Масив?", Array.isArray(res.data));
      console.log("Кількість записів:", res.data?.length);
      
      // Переконаємося, що дані - це масив
      if (Array.isArray(res.data)) {
        setData(res.data);
      } else {
        console.error("❌ Отримані дані не є масивом:", res.data);
        setData([]);
      }
      
      if (!res.data || res.data.length === 0) {
        console.warn("⚠️ Дані для цього тижня відсутні");
      }
    } catch (err) {
      console.error("❌ Помилка отримання даних:", err);
      if (err.response) {
        console.error("Статус:", err.response.status);
        console.error("Дані:", err.response.data);
        alert(`Помилка: ${err.response.data?.error || err.message}`);
      } else {
        alert(`Помилка мережі: ${err.message}`);
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWeek) fetchData(selectedWeek);
  }, [selectedWeek]);

  const sortData = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    console.log("📊 Маппінг даних, кількість:", data.length);
    if (data.length > 0) {
      console.log("Перший запис:", data[0]);
    }
    
    const sortable = [...data];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "ascending" ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    
    console.log("📤 Відсортовані дані для рендерингу:", sortable.length);
    return sortable;
  }, [data, sortConfig]);

  const getSortSymbol = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? " 🔼" : " 🔽";
    }
    return "";
  };

  const toggleStatus = (type, driverId, week) => {
    const key = `status_${type}_${driverId}_${week}`;
    const current = localStorage.getItem(key);
    localStorage.setItem(key, current === "true" ? "false" : "true");
    setData((prev) => [...prev]); // форс ререндер
  };

  const getStatusEmoji = (type, driverId, week) => {
    const key = `status_${type}_${driverId}_${week}`;
    return localStorage.getItem(key) === "true" ? "✅" : "❌";
  };

  const getStatusColor = (type, driverId, week) => {
    const key = `status_${type}_${driverId}_${week}`;
    return localStorage.getItem(key) === "true" ? "#d4edda" : "#f8d7da"; // зелений для виплачених, червоний для не виплачених
  };

  const getTextColor = (type, driverId, week) => {
    const key = `status_${type}_${driverId}_${week}`;
    return localStorage.getItem(key) === "true" ? "#155724" : "#721c24"; // темно-зелений для виплачених, темно-червоний для не виплачених
  };

  const handleDeleteReport = async (row) => {
    const confirmMsg = t("deleteReportConfirm", { 
      defaultValue: "Видалити рапорт водія {{name}} за тиждень {{week}}?",
      name: `${row.firstName} ${row.lastName}`,
      week: row.week
    });
    if (!window.confirm(confirmMsg)) return;

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`/api/driver-reports-table/${row.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData((prev) => prev.filter((item) => item.id !== row.id));
    } catch (err) {
      console.error("❌ Помилка видалення рапорту:", err);
      alert(err.response?.data?.error || t("error", { defaultValue: "Помилка" }));
    }
  };

  return (
    <div className="container">
      <h2>{t("driverReports")}</h2>

      <div className="d-flex align-items-center mb-3">
        <WeekSelector selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} />
        <button
          className="btn btn-primary ms-3"
          onClick={() => fetchData(selectedWeek)}
          disabled={!selectedWeek || loading}
        >
          {loading ? t("loading", { defaultValue: "Завантаження..." }) : t("loaddata")}
        </button>
      </div>

      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Завантаження...</span>
          </div>
          <p className="mt-2">Завантаження даних...</p>
        </div>
      )}

      {!loading && data.length === 0 && selectedWeek && (
        <div className="alert alert-info mt-3">
          Немає даних для тижня {selectedWeek}. Можливо, рапорти ще не згенеровані.
        </div>
      )}

      {!loading && sortedData.length > 0 && (
        <Table bordered hover className="mt-4 table-hover">
          <thead className="table-dark sticky-top text-center" style={{ zIndex: 10 }}>
            <tr>
              <th style={{ cursor: "pointer" }} onClick={() => sortData("firstName")}>
                {t("name")}{getSortSymbol("firstName")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => sortData("lastName")}>
                {t("surname")}{getSortSymbol("lastName")}
              </th>
              <th>{t("payout")}</th>
              <th>{t("extraFunds.sallary", { defaultValue: "Salary" })}</th>
              <th>{t("actions")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.firstName}</td>
                  <td>{row.lastName}</td>
                  <td 
                    style={{
                      backgroundColor: getStatusColor("payout", row.driverId, row.week),
                      cursor: "pointer"
                    }}
                    onDoubleClick={() => toggleStatus("payout", row.driverId, row.week)}
                    title="Подвійний клік для зміни статусу виплати"
                  >
                    {row.payout !== null && row.payout !== undefined ? `${row.payout.toFixed(2)} zł` : "-"}
                  </td>
                  <td 
                    style={{
                      backgroundColor: getStatusColor("sallary", row.driverId, row.week),
                      cursor: "pointer"
                    }}
                    onDoubleClick={() => toggleStatus("sallary", row.driverId, row.week)}
                    title="Подвійний клік для зміни статусу зарплати"
                  >
                    {row.sallary !== null && row.sallary !== undefined ? `${row.sallary.toFixed(2)} zł` : "-"}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary me-2"
                      onClick={() => navigate(`/admin/driver-report/${row.id}`)}
                    >
                      {t("view")}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteReport(row)}
                      title={t("delete")}
                    >
                      {t("delete")}
                    </button>
                  </td>
                  <td className="text-center">
                    <span
                      onDoubleClick={() => toggleStatus("payout", row.driverId, row.week)}
                      title="Подвійний клік для зміни статусу виплати"
                      style={{ 
                        cursor: "pointer", 
                        marginRight: 8,
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: getStatusColor("payout", row.driverId, row.week),
                        color: getTextColor("payout", row.driverId, row.week),
                        display: "inline-block",
                        minWidth: "60px"
                      }}
                    >
                      💸 {getStatusEmoji("payout", row.driverId, row.week)}
                    </span>
                    <span
                      onDoubleClick={() => toggleStatus("sallary", row.driverId, row.week)}
                      title="Подвійний клік для зміни статусу зарплати"
                      style={{ 
                        cursor: "pointer",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: getStatusColor("sallary", row.driverId, row.week),
                        color: getTextColor("sallary", row.driverId, row.week),
                        display: "inline-block",
                        minWidth: "60px"
                      }}
                    >
                      👷 {getStatusEmoji("sallary", row.driverId, row.week)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default DriverReportsTable;
